import React, { useState, useEffect, useRef } from 'react';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { Company } from '../../models/company.model';
import { Error } from '../../models/error.model';
import { MediaDA, PRESIGNED_UPLOAD_THRESHOLD_BYTES } from '../../services/admin/media.service';
import { GlobalSwal } from '../../styles/swal.style';
import { modalHide } from '../../utils/modal.util';
import { getError } from '../../languages/translations/response';
import { errorWebHandler } from '../../scripts/error.web.handler.script';

import AppLoading from '../Loading';

export interface AppModalDocumentUploadProps {
  companyForUser: Company | null,
  onSuccess: () => void
};

let errorResponse: Error;

const AppModalDocumentUpload: React.FunctionComponent<AppModalDocumentUploadProps> = ({companyForUser, onSuccess}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [file, setFile] = useState<File | null>(null)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const element = document.getElementById('modal-document-upload')
    if (!element) return

    const handleShown = () => {
      setFile(null)
      setLoadIndicator('off')
      setUploadProgress(null)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }

    element.addEventListener('shown.bs.modal', handleShown)
    return () => element.removeEventListener('shown.bs.modal', handleShown)
  }, [])

  const handleChangeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      setFile(event.target.files[0])
    }
  }

  const submitUpload = async () => {
    setLoadIndicator('on')
    setUploadProgress(null)

    if (companyForUser && file) {
      const showProgress = file.size > PRESIGNED_UPLOAD_THRESHOLD_BYTES
      const onProgress = showProgress ? (percent: number) => setUploadProgress(percent) : undefined

      await MediaDA.uploadDocumentSmart(companyForUser.id, file, 'web', onProgress).then( (response) => {
        if (response.status === 201) {
          modalHide('modal-document-upload')
          resetForm()

          GlobalSwal.fire({
            title: lang.labels.successfullyCreated,
            text: lang.labels.actionCompletedReturningToPage,
            icon: 'success',
            showConfirmButton: false,
            timer: 2200
          }).then( () => {
            onSuccess()
          })
        } else if (response.status === 202) {
          // ZIP extraction started in background
          modalHide('modal-document-upload')
          resetForm()

          GlobalSwal.fire({
            title: lang.labels.zipExtractionStarted,
            text: lang.labels.zipExtractionInProgress,
            icon: 'info',
            showConfirmButton: false,
            timer: 3000
          }).then( () => {
            onSuccess()
          })
        } else if (response.status === 413) {
          GlobalSwal.fire({
            title: lang.labels.uploadTooLarge,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        } else {
          errorResponse = response.data

          GlobalSwal.fire({
            title: getError(errorResponse.code, lang.code, errorResponse.message),
            text: lang.labels.sorryLooksLikeThereAreSomeErrorstryAgain,
            icon: 'error',
            confirmButtonText: lang.labels.okGotIt
          })
        }
      }).catch( (error) => {
        errorWebHandler(lang, error)
      }).finally(() => {
        setLoadIndicator('off')
        setUploadProgress(null)
      })
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        setLoadIndicator('off')
      })
    }
  }

  const resetForm = () => {
    setFile(null)
  }

  return (
    <div id="modal-document-upload" className="modal fade" tabIndex={-1} aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered mw-600px">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="modal-title">{lang.labels.uploadDocument}</h2>
            <div className="btn btn-sm btn-icon btn-active-color-primary" data-bs-dismiss="modal">
              <span className="svg-icon svg-icon-1">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <rect opacity="0.5" x="6" y="17.3137" width="16" height="2" rx="1" transform="rotate(-45 6 17.3137)" fill="black" />
                  <rect x="7.41422" y="6" width="16" height="2" rx="1" transform="rotate(45 7.41422 6)" fill="black" />
                </svg>
              </span>
            </div>
          </div>
          <div className="modal-body py-5 px-lg-10">
            <div className="row mb-5">
              <label className="form-label required">{lang.labels.file}</label>
              <div className="fv-row">
                <input ref={fileInputRef} className="form-control" type="file" onChange={handleChangeFile} />
              </div>
            </div>
            { uploadProgress !== null &&
              <div className="row mb-5">
                <div className="fv-row">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-muted fs-8">{lang.labels.uploadingFile}</span>
                    <span className="fw-bold fs-8">{uploadProgress}%</span>
                  </div>
                  <div className="progress" style={{height: '6px'}}>
                    <div className="progress-bar bg-primary" role="progressbar" style={{width: `${uploadProgress}%`}}></div>
                  </div>
                </div>
              </div>
            }
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-light me-3" type="button" data-bs-dismiss="modal">{lang.labels.cancel}</button>
            <button className="btn btn-primary" type="button" data-kt-indicator={loadIndicator} onClick={submitUpload}>
              <span className="indicator-label">{lang.labels.upload}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalDocumentUpload;
